/**
 * src/components/games/launchers/Quake3Launcher.tsx
 *
 * Quake 3 Arena — multiplayer voice-channel FPS via QuakeJS
 * (inolen/quakejs, MIT — a browser port of ioquake3 compiled with
 * Emscripten). The actual game runs inside a vendored same-origin shell at
 * `/quake3/index.html` (see public/quake3/) which boots the prebuilt
 * `ioquake3.js` client with an Emscripten callMain. The client connects
 * over WebSocket to a QuakeJS dedicated server (`ioq3ded.js`, default
 * port 27960) — everyone in the voice channel joins the SAME server and
 * frags each other.
 *
 * Server configuration
 * --------------------
 * Multiplayer needs a reachable QuakeJS dedicated server. Configure it
 * with the build-time env vars (documented in .env.example):
 *
 *   VITE_QUAKE3_SERVER   e.g. "play.psydeserver.com:27960"
 *                        (omit protocol; WebSocket implied). Empty →
 *                        players use the in-game server browser / connect
 *                        command instead of auto-joining.
 *   VITE_QUAKE3_CONTENT  e.g. "https://content.psydeserver.com"
 *                        (game assets pk3). The public content.quakejs.com
 *                        is currently unreachable (HTTP 526) so production
 *                        deployments MUST self-host a content server —
 *                        see THIRD_PARTY_ASSETS.md. Empty → falls back to
 *                        the public content server inside the shell.
 *
 * The arcade flow: `multiplayer: true` + `solo: true` — the host's
 * "Host Multiplayer" broadcasts change-game so every VC peer mounts this
 * same activity (all auto-connecting to the shared server), while "Play
 * Solo" mounts the iframe locally without a broadcast (a graceful
 * fallback when the socket.io coordination server is down — the game
 * itself still reaches the QuakeJS server).
 */

import React from 'react';
import { Crosshair } from 'lucide-react';
import { createIframeGame } from './IframeGameActivity';

/**
 * Build-time env overrides. `import.meta.env` is statically inlined by
 * Vite, so these are baked at build time (not runtime). Reading via the
 * safe `?? ''` pattern keeps the arcade tile working when unset.
 */
const QUAKE3_SERVER = (import.meta.env.VITE_QUAKE3_SERVER as string | undefined) ?? '';
const QUAKE3_CONTENT = (import.meta.env.VITE_QUAKE3_CONTENT as string | undefined) ?? '';
// OpenArena (free, GPL game data) is the default mod — same ioquake3
// engine, no proprietary Quake 3 Arena assets required. `?fs_game%20oa`
// tells the client which pak set to load from the content server.
const QUAKE3_GAME = (import.meta.env.VITE_QUAKE3_GAME as string | undefined) ?? 'oa';

/**
 * Assemble the same-origin shell URL.
 *
 *  - `?connect%20host:port` — the QuakeJS query-arg convention (space-
 *    encoded, matching quakejs.com's /play?connect%20IP:27960). The
 *    shell's getQueryCommands() turns this into `+connect host:port`.
 *  - `?content=https://...`  — explicit content-server override. The
 *    shell parses this directly.
 *  - `?fs_game%20oa`         — OpenArena mod dir; the shell passes it
 *    through to the engine so the client fetches the right paks.
 *
 * When VITE_QUAKE3_SERVER is unset the URL carries no connect param so
 * the client boots to the main menu (join via in-game browser).
 */
const params: string[] = [];
if (QUAKE3_CONTENT) params.push(`content=${encodeURIComponent(QUAKE3_CONTENT)}`);
if (QUAKE3_GAME) params.push(`fs_game%20${encodeURIComponent(QUAKE3_GAME)}`);
if (QUAKE3_SERVER) params.push(`connect%20${encodeURIComponent(QUAKE3_SERVER)}`);
const QUAKE3_URL = '/quake3/index.html' + (params.length ? `?${params.join('&')}` : '');

export default createIframeGame({
  id: 'quake3',
  displayName: 'Quake 3 Arena',
  shortDescription:
    'Multiplayer arena FPS (OpenArena) — everyone in the call joins the same server.',
  // No asymmetric scoreboard for v1 — QuakeJS has its own in-game
  // stats (frags, arena wins). Leaderboard wiring is a follow-up.
  leaderboard: null,
  sameOrigin: true,
  // Multiplayer is handled INSIDE the iframe (WebSocket → dedicated
  // ioq3ded.js server). The framework's socket.io flow is only the
  // arcade coordination layer.
  multiplayer: true,
  multiplayerShape: {
    maxPlayers: 16,
    // The framework's GameActivityManager was never built, so this is
    // advisory metadata for the arcade. The real transport is the
    // QuakeJS WebSocket protocol to the dedicated server.
    transport: 'socket.io',
    requiresVoiceChannel: true,
    hostMigration: 'auto',
  },
  solo: true,
  url: QUAKE3_URL,
  icon: Crosshair,
  attribution: {
    text:
      'QuakeJS — MIT — github.com/inolen/quakejs  ·  Engine: ioquake3 (GPL-2.0)  ·  Game data: OpenArena (GPL-2.0)  ·  Original game © id Software',
    license: 'MIT',
    href: 'https://github.com/inolen/quakejs',
  },
});
